import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, Wifi, AlertCircle, CheckCircle2 } from 'lucide-react';

/**
 * OfflineIndicator - Enhanced component that shows network status
 * Displays banners for offline/online transitions with better UX
 */
const OfflineIndicator = ({ appTheme = 'blue' }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastOnlineTime, setLastOnlineTime] = useState(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOnlineMessage(true);
      setLastOnlineTime(null);
      
      // Play success sound if available
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjaH0fPTgjMGHm7A7+OZURE');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowOnlineMessage(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOnlineMessage(false);
      setLastOnlineTime(new Date());
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);
    
    // Try to fetch a small resource to check connectivity
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      await fetch('/manifest.json', { 
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // If successful, trigger online event manually
      if (!navigator.onLine) {
        window.dispatchEvent(new Event('online'));
      }
    } catch (error) {
      console.log('Still offline');
    } finally {
      setTimeout(() => {
        setIsRetrying(false);
      }, 1000);
    }
  };

  const getOfflineDuration = () => {
    if (!lastOnlineTime) return '';
    const minutes = Math.floor((new Date() - lastOnlineTime) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    return `${hours} hours ago`;
  };

  // Show success message when back online
  if (showOnlineMessage) {
    return (
      <div
        className={
          appTheme === 'white'
            ? 'fixed top-0 left-0 right-0 glass-card-white-theme p-3 shadow-lg z-50 animate-slide-down'
            : 'fixed top-0 left-0 right-0 bg-[#ff3f6c] text-white p-3 shadow-lg z-50 animate-slide-down'
        }
      >
        <div className="max-w-md mx-auto flex items-center justify-center gap-3">
          <CheckCircle2 className="w-6 h-6 animate-bounce" />
          <div>
            <p className="font-bold text-sm" style={appTheme === 'white' ? { color: '#222' } : {}}>You're back online!</p>
            <p className="text-xs" style={appTheme === 'white' ? { color: '#388e3c' } : {}}>All features are now available</p>
          </div>
        </div>
      </div>
    );
  }

  // Show offline banner
  if (!isOnline) {
    return (
      <div
        className={
          appTheme === 'white'
            ? 'fixed top-0 left-0 right-0 glass-card-white-theme p-3 shadow-lg z-50 animate-slide-down'
            : 'fixed top-0 left-0 right-0 bg-[#ff3f6c] text-white p-3 shadow-lg z-50 animate-slide-down'
        }
      >
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 flex-1">
              <WifiOff className="w-5 h-5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm" style={appTheme === 'white' ? { color: '#b71c1c' } : {}}>No Internet Connection</p>
                <p className="text-xs" style={appTheme === 'white' ? { color: '#e65100' } : {}}>
                  {lastOnlineTime ? `Offline ${getOfflineDuration()}` : 'Check your network settings'}
                </p>
              </div>
            </div>
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className={appTheme === 'white' ? 'bg-gray-100 hover:bg-gray-200 p-2 rounded-lg transition-all disabled:opacity-50 flex-shrink-0' : 'bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-all disabled:opacity-50 flex-shrink-0'}
              aria-label="Retry connection"
            >
              <RefreshCw className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Offline capabilities info */}
          <div className={appTheme === 'white' ? 'bg-gray-50 rounded-lg p-2 mt-2' : 'bg-white/10 backdrop-blur-sm rounded-lg p-2 mt-2'}>
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs" style={appTheme === 'white' ? { color: '#b71c1c' } : {}}>
                You can still browse cached products and view your order history
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OfflineIndicator;
